import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  sendEmailVerification,
  type AuthError,
} from 'firebase/auth'
import { auth, IS_FIREBASE_ENABLED } from '../lib/firebase'
import { api } from '../lib/api'
import { setSession, getAnonymousSession, clearAnonymousSession, revokeAnonymousSession, type DemoStudent } from '../lib/auth'
import { track } from '../lib/analytics'
import { useOffline } from '../hooks/useOffline'
import { useAuth } from '../hooks/useAuth'

// ── Types ─────────────────────────────────────────────────────────────────────

interface VerifyResponse  { token: string; student: DemoStudent }
interface RegisterResponse { student: DemoStudent }

const WELCOME_BACK_VARIANTS = [
  'Bom ver-te de novo.',
  'Voltaste - vamos a isso.',
  'Aí estás. Continuamos.',
  'Estávamos à tua espera.',
] as const

// ── Firebase error messages → Portuguese ─────────────────────────────────────

function firebaseErrorPt(code: string): string {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
      return 'Email ou senha incorrectos. Tenta novamente.'
    case 'auth/wrong-password':
      return 'Senha incorrecta. Tenta novamente.'
    case 'auth/email-already-in-use':
      return 'Este email já está registado. Entra com a tua senha.'
    case 'auth/weak-password':
      return 'A senha deve ter pelo menos 6 caracteres.'
    case 'auth/invalid-email':
      return 'Email inválido. Verifica e tenta novamente.'
    case 'auth/too-many-requests':
      return 'Demasiadas tentativas. Aguarda alguns minutos e tenta novamente.'
    case 'auth/network-request-failed':
      return 'Sem ligação à internet. Verifica a tua conexão.'
    default:
      return 'Erro ao entrar. Tenta novamente.'
  }
}

// Routes each user back into the correct student/admin/teacher flow after login.
async function postLoginRoute(): Promise<string> {
  try {
    const me = await api.get<{
      student: {
        onboarded: boolean
        placement_complete: boolean
        role?: string
        is_admin: boolean
        onboarding_style_v2?: boolean
        has_programme?: boolean
      }
    }>('/auth/me')
    const s = me.student

    if (s.role === 'org_manager')            return '/organization'
    if (s.role === 'teacher')                return '/teacher'
    if (s.role === 'admin' || s.is_admin)    return '/admin'

    if (s.onboarding_style_v2 === false) {
      if (!s.onboarded) return '/onboarding'
      if (!s.placement_complete) {
        try {
          const status = await api.get<{
            placement_complete: boolean
            subjects: { code: string; completed: boolean }[]
          }>('/placement/status')
          const next = status.subjects.find(x => !x.completed)
          if (next && !status.placement_complete) return `/placement?subject=${next.code}`
        } catch { /* fall through */ }
      }
      return '/subjects'
    }

    if (!s.has_programme) return '/universities'
    return '/subjects'
  } catch {
    return '/subjects'
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export const LoginPage: React.FC = () => {
  const [params]                = useSearchParams()
  // Pre-fill email from deep links such as teacher activation.
  const [name,     setName]     = useState('')
  const [email,    setEmail]    = useState(() => params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isNew,    setIsNew]    = useState(false)
  const [isRecovering, setIsRecovering] = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [info,     setInfo]     = useState<string | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [welcomeBack] = useState<string>(
    () => WELCOME_BACK_VARIANTS[Math.floor(Math.random() * WELCOME_BACK_VARIANTS.length)],
  )
  // First-time login needs network; returning students are usually rehydrated earlier.
  const isOffline = useOffline()

  const navigate = useNavigate()
  const { refresh } = useAuth()

  const settleSharedAuth = async () => {
    for (let i = 0; i < 3; i += 1) {
      const next = await refresh()
      if (next) return next
      await new Promise(resolve => setTimeout(resolve, 150))
    }
    return null
  }

  // ── Google sign-in ───────────────────────────────────────────────────────
  const handleGoogle = async () => {
    if (!auth) return
    setLoading(true)
    setError(null)
    setInfo(null)
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
      const settled = await settleSharedAuth()
      if (settled && !settled.is_anonymous) {
        clearAnonymousSession()
      }
      navigate(await postLoginRoute())
    } catch (err) {
      const ae = err as AuthError
      if (ae.code !== 'auth/popup-closed-by-user' && ae.code !== 'auth/cancelled-popup-request') {
        setError(firebaseErrorPt(ae.code))
      }
    } finally {
      setLoading(false)
    }
  }

  // ── Firebase flow ────────────────────────────────────────────────────────
  const handleFirebase = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!auth) return
    setLoading(true)
    setError(null)
    setInfo(null)

    try {
      if (isNew) {
        // Keep the mismatch guard on the submit path too.
        if (password !== confirmPassword) {
          setError('As senhas não coincidem.')
          setLoading(false)
          return
        }
        const cred = await createUserWithEmailAndPassword(auth, email, password)

        // Best-effort so the backend receives the typed display name.
        if (name && cred.user) {
          try {
            await updateProfile(cred.user, { displayName: name })
            await cred.user.getIdToken(true)
          } catch {
            /* non-fatal */
          }
        }

        try {
          await sendEmailVerification(cred.user)
        } catch {
          /* non-fatal: rate-limited or transient - banner CTA covers it */
        }

        track('signup_completed', { source: 'email' })
      } else {
        await signInWithEmailAndPassword(auth, email, password)
        const settled = await settleSharedAuth()
        if (settled && !settled.is_anonymous) {
          clearAnonymousSession()
        }
      }
      navigate(await postLoginRoute())
    } catch (err) {
      const ae = err as AuthError
      if (ae.code === 'auth/user-not-found' && !isNew) {
        setIsNew(true)
        setError('Email não encontrado. Preenche o teu nome e cria a tua conta.')
      } else {
        setError(firebaseErrorPt(ae.code))
      }
    } finally {
      setLoading(false)
    }
  }

  // ── Demo flow (DEMO_MODE - no Firebase credentials) ──────────────────────
  const handleDemo = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setInfo(null)

    try {
      if (isNew) {
        await api.post<RegisterResponse>('/auth/register', {
          email,
          password: 'demo1234',
          name: name || email.split('@')[0],
        })
      }

      const data = await api.post<VerifyResponse>('/auth/verify', { email })
      setSession({ token: data.token, student: data.student })
      await settleSharedAuth()
      navigate(await postLoginRoute())
    } catch (err: any) {
      if (err.status === 404 && !isNew) {
        setIsNew(true)
        setError('Email não encontrado. Preenche o teu nome e tenta novamente.')
      } else {
        setError(err.message ?? 'Erro ao entrar. Tenta novamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = IS_FIREBASE_ENABLED ? handleFirebase : handleDemo

  const handleRequestPasswordReset = async () => {
    if (!IS_FIREBASE_ENABLED || loading) return
    if (!email.trim()) {
      setError('Escreve primeiro o teu email para te enviarmos o link de recuperação.')
      setInfo(null)
      return
    }

    setLoading(true)
    setError(null)
    setInfo(null)

    try {
      await api.post('/auth/request-password-reset', { email: email.trim() })
      setInfo('Enviámos um email com instruções para redefinires a tua senha. Vai ao teu email e cria uma nova senha por lá.')
    } catch {
      setInfo('Enviámos um email com instruções para redefinires a tua senha. Vai ao teu email e cria uma nova senha por lá.')
    } finally {
      setLoading(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-5 sm:p-8">

        <div className="text-center mb-8">
          <img
            src="/brand/logo.svg"
            alt="Tutor"
            className="h-12 sm:h-14 mx-auto mb-2"
          />
          <h1 className="sr-only">Tutor</h1>
          <p className="text-gray-500 mt-1.5 text-sm">
            {/* Voice Foundation PR (2026-05-03). When in sign-in mode,
                show a rotating welcome-back greeting instead of the
                static "Preparação para Exames". Brand-new users
                (signup) keep the descriptive subtitle. See
                WELCOME_BACK_VARIANTS for the rotation set. */}
            {isNew ? 'Preparação para Exames' : welcomeBack}
          </p>
        </div>

        {/* Offline banner - only shown to first-time users who land here
            without a network. Returning students bypass the login page
            entirely because Firebase's IndexedDB-cached auth state
            keeps them signed in. */}
        {isOffline && (
          <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-lg leading-none mt-0.5 shrink-0" aria-hidden>📵</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Sem net agora</p>
              <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                Para entrares pela primeira vez precisas de net. Liga-te à WiFi ou aos dados móveis e voltamos.
              </p>
            </div>
          </div>
        )}

        {/* Google sign-in - Firebase mode only */}
        {IS_FIREBASE_ENABLED && !isRecovering && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading || isOffline}
              title={isOffline ? 'Precisa de conexão à internet' : ''}
              className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-sm"
            >
              {/* Google logo SVG */}
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </button>

            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </>
        )}

        <form onSubmit={isRecovering ? ((e) => { e.preventDefault(); void handleRequestPasswordReset() }) : handleSubmit} className="space-y-4">

          {/* Name - shown only when creating a new account */}
          {isNew && !isRecovering && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                O teu nome
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome completo"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (error) setError(null)
                if (info) setInfo(null)
              }}
              required
              placeholder="exemplo@email.com"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            />
          </div>

          {/* Password - Firebase mode only.
              Show/hide eye toggle is available on BOTH signup and
              signin (2026-06-15). Students often mistype the password
              silently against the dots; flipping to plain text reduces
              failed-login frustration on signin and avoids the
              "I created an account with a password I can't remember
              because I typo'd it" trap on signup. Confirmar senha
              stays signup-only — repeating during signin adds friction
              with no security benefit. */}
          {IS_FIREBASE_ENABLED && !isRecovering && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (error) setError(null)
                    if (info) setInfo(null)
                  }}
                  required
                  placeholder={isNew ? 'Mínimo 6 caracteres' : '••••••••'}
                  minLength={isNew ? 6 : undefined}
                  className="w-full pl-4 pr-11 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                  className="absolute right-0 top-0 h-full px-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3l18 18" />
                      <path d="M10.585 10.587a2 2 0 002.828 2.83" />
                      <path d="M9.363 5.365A9.466 9.466 0 0112 5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228A10.45 10.45 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {!isNew && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setIsRecovering(true)
                      setIsNew(false)
                      setError(null)
                      setInfo(null)
                      setPassword('')
                      setConfirmPassword('')
                      setShowPassword(false)
                      setShowConfirmPassword(false)
                    }}
                    disabled={loading || isOffline}
                    className="text-sm text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Esqueceste a senha?
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Confirmar senha — signup only */}
          {IS_FIREBASE_ENABLED && isNew && !isRecovering && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Repete a senha"
                  minLength={6}
                  className={`w-full pl-4 pr-11 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm ${
                    confirmPassword && confirmPassword !== password ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(p => !p)}
                  aria-label={showConfirmPassword ? 'Esconder senha' : 'Mostrar senha'}
                  className="absolute right-0 top-0 h-full px-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3l18 18" />
                      <path d="M10.585 10.587a2 2 0 002.828 2.83" />
                      <path d="M9.363 5.365A9.466 9.466 0 0112 5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228A10.45 10.45 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <p className="text-xs text-red-600 mt-1">As senhas não coincidem.</p>
              )}
            </div>
          )}

          {isRecovering && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
              Escreve o teu email e vamos enviar-te um link para criares uma nova senha.
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {info && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={
              loading
              || isOffline
              || !email
              || (!isRecovering && IS_FIREBASE_ENABLED && !password)
              || (!isRecovering && IS_FIREBASE_ENABLED && isNew && password !== confirmPassword)
            }
            title={isOffline ? 'Precisa de conexão à internet' : ''}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
          >
            {loading ? (isRecovering ? 'A enviar…' : 'A entrar…') : isRecovering ? 'Enviar email de recuperação' : isNew ? 'Criar conta' : 'Vamos lá'}
          </button>

          {/* Toggle between sign in / sign up */}
          {isRecovering ? (
            <p className="text-center text-sm text-gray-500">
              Lembraste da senha?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsRecovering(false)
                  setError(null)
                  setInfo(null)
                }}
                className="text-blue-600 font-medium hover:underline"
              >
                Voltar à entrada
              </button>
            </p>
          ) : !isNew ? (
            <p className="text-center text-sm text-gray-500">
              Não tens conta?{' '}
              <button
                type="button"
                onClick={() => { setIsNew(true); setIsRecovering(false); setError(null); setInfo(null); setConfirmPassword(''); setShowPassword(false); setShowConfirmPassword(false) }}
                className="text-blue-600 font-medium hover:underline"
              >
                Criar conta
              </button>
            </p>
          ) : (
            <p className="text-center text-sm text-gray-500">
              Já tens conta?{' '}
              <button
                type="button"
                onClick={() => { setIsNew(false); setIsRecovering(false); setError(null); setInfo(null); setConfirmPassword(''); setShowPassword(false); setShowConfirmPassword(false) }}
                className="text-blue-600 font-medium hover:underline"
              >
                Entrar
              </button>
            </p>
          )}
        </form>

        {/* Shared-device reset (Slice 10 / decision H, 2026-05-19 fix
            close-out). Only renders when there's an active anonymous
            session in localStorage - otherwise it's noise for
            returning users who never browsed anonymously. The handler
            mirrors AnonymousSignupCard's "Não sou eu" path: drop the
            anon JWT, reload to / so useAnonymousBootstrap fires fresh
            on the next entry route. Mozambican shared-device reality
            justifies surfacing this on /login too: a student who
            switches phones / cybercafes lands on /login if they
            already have an account, and may inherit a stranger's
            anonymous session on the same browser. */}
        {getAnonymousSession() !== null && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Estás num dispositivo partilhado?{' '}
            <button
              type="button"
              onClick={async () => {
                track('anon_funnel.shared_device_reset', { source: 'login_page' })
                await revokeAnonymousSession().catch(() => clearAnonymousSession())
                window.location.href = '/'
              }}
              className="text-gray-500 underline hover:text-gray-700"
            >
              Limpar a sessão anónima
            </button>
          </p>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          {IS_FIREBASE_ENABLED ? 'Tutor' : 'Modo de demonstração'}
        </p>
      </div>
    </div>
  )
}

export default LoginPage
